import { useEffect, useState } from "react";
import { getNotifications, sendNotification, getAllPromoters, getAllSupervisors, addAuditLog } from "../../firebase/services";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { storage } from "../../firebase/config";
import { format } from "date-fns";
import { useLanguage } from "../../hooks/useLanguage";
import { useAuth } from "../../hooks/useAuth";

export default function AdminNotifications() {
  const { t } = useLanguage();
  const { profile } = useAuth();
  const [notifs, setNotifs] = useState([]);
  const [promoters, setPromoters] = useState([]);
  const [supervisors, setSupervisors] = useState([]);
  const [form, setForm] = useState({ title: "", body: "", target: "all" });
  const [selected, setSelected] = useState([]);
  const [file, setFile] = useState(null);
  const [sending, setSending] = useState(false);
  const [msg, setMsg] = useState("");

  const load = async () => setNotifs(await getNotifications());
  useEffect(() => {
    load();
    getAllPromoters().then(setPromoters);
    getAllSupervisors().then(setSupervisors);
  }, []);

  const toggleSelect = (uid) =>
    setSelected(s => s.includes(uid) ? s.filter(x => x !== uid) : [...s, uid]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { setMsg(t("notif.empty_fields")); return; }
    if (form.target === "specific" && selected.length === 0) { setMsg(t("notif.select_at_least")); return; }
    setSending(true);
    setMsg("");
    try {
      let fileUrl = null;
      if (file) {
        const r = ref(storage, `notifications/${Date.now()}_${file.name}`);
        await uploadBytes(r, file);
        fileUrl = await getDownloadURL(r);
      }
      let sentTo;
      if (form.target === "all") sentTo = "all";
      else if (form.target === "supervisors") sentTo = "supervisors";
      else if (form.target === "promoters_all") sentTo = promoters.map(p => p.uid);
      else sentTo = selected;

      await sendNotification({ title: form.title, body: form.body, fileUrl, sentTo });
      const sentToLabel = sentTo === "all" ? "tất cả" : sentTo === "supervisors" ? "supervisors" : `${Array.isArray(sentTo) ? sentTo.length : 1} người`;
      addAuditLog({ type: "notification_sent", actorName: profile?.name || "Admin", actorRole: "admin", detail: `Gửi thông báo: "${form.title}" đến ${sentToLabel}`, meta: { sentTo } });
      setMsg(t("notif.sent_ok"));
      setForm({ title: "", body: "", target: "all" });
      setSelected([]);
      setFile(null);
      load();
    } catch (err) {
      setMsg("Lỗi: " + err.message);
    } finally {
      setSending(false);
      setTimeout(() => setMsg(""), 4000);
    }
  };

  const allCount = promoters.length + supervisors.length;

  return (
    <>
      <div className="page-header"><h1>{t("notif.title")}</h1><p>{t("notif.subtitle")}</p></div>
      {msg && <div className={`alert ${msg.startsWith("✅") ? "alert-success" : "alert-error"}`}>{msg}</div>}
      <div className="grid-2">
        <div className="card">
          <div className="card-title">{t("notif.submit_title")}</div>
          <form onSubmit={handleSend}>
            <div className="form-group">
              <label className="form-label">{t("notif.title_field")}</label>
              <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder={t("notif.placeholder_title")} required />
            </div>
            <div className="form-group">
              <label className="form-label">{t("notif.body")}</label>
              <textarea rows={3} value={form.body} onChange={e => setForm(f => ({ ...f, body: e.target.value }))} placeholder={t("notif.placeholder_body")} style={{ width: "100%", padding: "8px 12px", borderRadius: 8, border: "1px solid #e5e7eb", fontSize: 14, resize: "vertical" }} />
            </div>
            <div className="form-group">
              <label className="form-label">{t("notif.send_to")}</label>
              <select value={form.target} onChange={e => { setForm(f => ({ ...f, target: e.target.value })); setSelected([]); }}>
                <option value="all">{t("notif.all_promoters")} + {t("notif.all_supervisors")} ({allCount})</option>
                <option value="supervisors">{t("notif.all_supervisors")} ({supervisors.length})</option>
                <option value="promoters_all">{t("notif.all_promoters")} ({promoters.length})</option>
                <option value="specific">{t("notif.select_specific")}</option>
              </select>
            </div>
            {form.target === "specific" && (
              <div className="form-group">
                <label className="form-label">{t("notif.select_promoters")} ({selected.length} đã chọn)</label>
                <div style={{ maxHeight: 200, overflowY: "auto", border: "1px solid #e5e7eb", borderRadius: 8, padding: "6px 10px" }}>
                  {supervisors.length > 0 && (
                    <>
                      <div style={{ fontSize: 11, fontWeight: 600, color: "#888", padding: "4px 0 2px" }}>SUPERVISORS</div>
                      {supervisors.map(p => (
                        <label key={p.uid} style={{ display: "flex", alignItems: "center", gap: 8, padding: "3px 0", cursor: "pointer" }}>
                          <input type="checkbox" checked={selected.includes(p.uid)} onChange={() => toggleSelect(p.uid)} />
                          <span style={{ fontSize: 13 }}>🎖 {p.name}</span>
                        </label>
                      ))}
                      <div style={{ borderTop: "1px solid #e5e7eb", margin: "4px 0" }} />
                    </>
                  )}
                  <div style={{ fontSize: 11, fontWeight: 600, color: "#888", padding: "4px 0 2px" }}>PROMOTERS</div>
                  {promoters.map(p => (
                    <label key={p.uid} style={{ display: "flex", alignItems: "center", gap: 8, padding: "3px 0", cursor: "pointer" }}>
                      <input type="checkbox" checked={selected.includes(p.uid)} onChange={() => toggleSelect(p.uid)} />
                      <span style={{ fontSize: 13 }}>{p.name} <span style={{ color: "#aaa", fontSize: 11 }}>{p.shopName}</span></span>
                    </label>
                  ))}
                </div>
              </div>
            )}
            <div className="form-group">
              <label className="form-label">{t("notif.upload_file")}</label>
              <input type="file" onChange={e => setFile(e.target.files[0])} style={{ border: "none", padding: 0 }} />
            </div>
            <button className="btn btn-primary btn-full" type="submit" disabled={sending}>
              {sending ? t("common.sending") : t("notif.send_btn")}
            </button>
          </form>
        </div>
        <div className="card">
          <div className="card-title">{t("sales.history")}</div>
          {notifs.map(n => (
            <div key={n.id} className="notif-item">
              <div className="notif-icon">{n.fileUrl ? "📎" : "📢"}</div>
              <div className="notif-content">
                <div className="notif-title">{n.title}</div>
                <div className="notif-sub">
                  {n.createdAt?.toDate ? format(n.createdAt.toDate(), "dd/MM/yyyy HH:mm") : ""}
                  {n.sentTo && n.sentTo !== "all" && (
                    <span style={{ marginLeft: 8, fontSize: 11, color: "#888" }}>
                      → {n.sentTo === "supervisors" ? t("notif.all_supervisors") : Array.isArray(n.sentTo) ? `${n.sentTo.length} người` : n.sentTo}
                    </span>
                  )}
                </div>
                {n.body && <div style={{ fontSize: 12, color: "#888", marginTop: 4 }}>{n.body}</div>}
              </div>
            </div>
          ))}
          {notifs.length === 0 && <div style={{ color: "#aaa", fontSize: 13, padding: "12px 0" }}>{t("notif.no_notifs")}</div>}
        </div>
      </div>
    </>
  );
}
